import { Button, Col, Form, Input, theme, Typography } from "antd";
import { useEffect, useState } from "react";
import { resetPassword } from "../repository/auth.repository";
import InModal from "../controls/InModal";

export interface ResetPasswordDialogProps {
  readonly open?: boolean;
  readonly code: string;
  readonly userUid: string;
  readonly onComplete?: () => void;
  readonly onCancel?: () => void;
}

function ResetPasswordDialog(props: ResetPasswordDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<any>([], form);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [valuesValid, setValuesValid] = useState(false);
  const [valuesChange, setValuesChange] = useState<any>({});

  useEffect(() => {
    reset();
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(() => {
        setValuesValid(true);
      })
      .catch(() => {
        setValuesValid(false);
      });
    setValuesChange(values);
  }, [form, values, props.open]);

  function onFormChange(): void {
    setError(false);
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    resetPassword({
      code: props.code,
      userUid: props?.userUid,
      oldPassword: valuesChange.oldPassword,
      newPassword: valuesChange.newPassword,
    })
      .then(() => {
        setLoading(false);
        props.onComplete?.();
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValuesValid(false);
    setValuesChange({});
    setError(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      title={"Reset Password"}
      onCancel={onCancel}
      size={"small"}
      actions={[
        <Button
          key={"close"}
          className="ml-2"
          disabled={loading}
          onClick={onCancel}
        >
          Close
        </Button>,
        <Button
          key={"change"}
          type="primary"
          loading={loading}
          disabled={!valuesValid}
          onClick={onSubmit}
        >
          Change
        </Button>,
      ]}
    >
      <Col className="flex flex-col items-center">
        <Form
          className="my-form-item flex w-72 flex-col"
          form={form}
          layout={"vertical"}
          variant={"filled"}
          colon={false}
          autoComplete="off"
          initialValues={{}}
          onChange={onFormChange}
        >
          <Form.Item
            className="mb-2"
            name={"oldPassword"}
            label={
              <Typography.Text
                className="text-sm"
                style={{
                  color: token.colorTextDescription,
                }}
              >
                Old Password
              </Typography.Text>
            }
            rules={[
              {
                required: true,
                message: "Old password is required",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            className="mb-2"
            name={"newPassword"}
            label={
              <Typography.Text
                className="text-sm"
                style={{
                  color: token.colorTextDescription,
                }}
              >
                New Password
              </Typography.Text>
            }
            rules={[
              {
                required: true,
                message: "New password is required",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("oldPassword") !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Repeat password cannot be reused"),
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            className="mb-2"
            name={"confirmPassword"}
            label={
              <Typography.Text
                className="text-sm"
                style={{
                  color: token.colorTextDescription,
                }}
              >
                Confirm Password
              </Typography.Text>
            }
            dependencies={["newPassword"]}
            rules={[
              {
                required: true,
                message: "Confirm password is required",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("New password mismatch"));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
        {error && (
          <Typography.Text className="mt-4 text-center" type="danger">
            Something went wrong, check fields
          </Typography.Text>
        )}
      </Col>
    </InModal>
  );
}

export default ResetPasswordDialog;
